import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { CacheKeys } from '@/lib/cache'
import type { FillCardForm } from '../App/Customers/FillApplication/useFillCardSave'

const ROSTER_KEYS: readonly unknown[] = [CacheKeys.DRIVERS, CacheKeys.TRUCKS, CacheKeys.TRAILERS]

/**
 * The Equipment dialog's Save: drivers and trucks save in their own dialogs as they're edited, so there is
 * nothing left to send, but Save lights up once a roster has changed since the dialog opened. Closing it
 * loses nothing, so Esc and a click outside still close it.
 */
export function useRosterEdits(orgId: string, open: boolean): FillCardForm {
  const queryClient = useQueryClient()
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    if (!open) return
    setChanged(false)
    // Each roster query's data as first seen; unchanged data keeps its reference across refetches.
    const seen = new Map<string, unknown>()
    for (const query of queryClient.getQueryCache().getAll()) {
      const [key, keyOrgId] = query.queryKey
      if (keyOrgId === orgId && ROSTER_KEYS.includes(key) && query.state.data !== undefined) {
        seen.set(query.queryHash, query.state.data)
      }
    }
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return
      const [key, keyOrgId] = event.query.queryKey
      if (keyOrgId !== orgId || !ROSTER_KEYS.includes(key)) return
      const data = event.query.state.data
      const previous = seen.get(event.query.queryHash)
      seen.set(event.query.queryHash, data)
      // A roster loading for the first time as the dialog mounts it, or its next page arriving, isn't an edit.
      if (previous !== undefined && previous !== data && !isNextPage(previous, data)) setChanged(true)
    })
  }, [open, orgId, queryClient])

  return {
    isDirty: changed,
    unsaved: false,
    saving: false,
    save: () => Promise.resolve(true),
    discard: () => undefined,
  }
}

/** Only more pages came: a refetch keeps an unchanged page's reference, so an edit replaces one already seen. */
function isNextPage(previous: unknown, next: unknown): boolean {
  const before = pagesOf(previous)
  const after = pagesOf(next)
  return (
    !!before &&
    !!after &&
    after.length > before.length &&
    before.every((page, index) => after[index] === page)
  )
}

function pagesOf(data: unknown): unknown[] | undefined {
  if (typeof data !== 'object' || data === null || !('pages' in data)) return undefined
  return Array.isArray(data.pages) ? data.pages : undefined
}
